import { TitleSectionDataHandler } from '@mentimeter/question-modules-shared';
import type { ModeT } from '@mentimeter/question-modules-types';
import { usePresentationContext } from '@mentimeter/question-modules-contexts';
import type { Question } from '@mentimeter/http-clients';
import { Box } from '@mentimeter/ragnar-ui/box';
import { ScreenshotViewDataHandler } from './presentation/ScreenshotView';
import { StartVotingView } from './presentation/StartVotingView';
import { ShowGroupDetailViewProvider } from './presentation/utils/useShowGroupDetailView';
import { GroupsWithVotesDataHandler } from './presentation/grouping/groups-with-votes-view/GroupsWithVotesDataHandler';
import { GroupsWithoutVotesDataHandler } from './presentation/grouping/groups-without-votes-view/GroupsWithoutVotesDataHandler';
import { EditorEmptyState } from './presentation/EditorEmptyState';
import { TitleVoteCounter } from './presentation/TitleVoteCounter';
import { Visualization } from './presentation/Visualization';
import type { VisualizationState } from './presentation/utils/getVisualizationState';
import type { OpenEndedResponse } from './presentation/types';

const PREVIEW_DATA_EMPTY_TITLE = '&nbsp;';

interface PresentationModuleProps {
  visualizationState: VisualizationState;
  mode: ModeT;
  shouldHideResults: boolean;
  responses: OpenEndedResponse[];
  numberOfVoters: number;
  subType: Question['sub_type'];
  layout: Question['layout'];
  questionTitle: Question['question'];
  isUpvotingEnabled: Question['voting_on_answers'];
}
export const PresentationModule = ({
  visualizationState,
  mode,
  shouldHideResults,
  responses,
  numberOfVoters,
  subType,
  layout,
  questionTitle,
  isUpvotingEnabled,
}: PresentationModuleProps) => {
  const { InteractiveCanvasComponents } = usePresentationContext();

  const numberOfAnswers = responses.length;
  const numberOfAnswersWithUpvotes = responses.filter(
    (v) => v.voteCount > 0,
  ).length;

  const shouldShowVoteCounterText =
    questionTitle.length > 0 &&
    questionTitle !== PREVIEW_DATA_EMPTY_TITLE &&
    !shouldHideResults;

  if (mode === 'screenshot') {
    return (
      <ScreenshotViewDataHandler
        responses={responses}
        votingOnAnswers={isUpvotingEnabled}
      />
    );
  }

  if (visualizationState === 'start-voting') {
    return <StartVotingView numberOfVoters={numberOfVoters} />;
  }

  const ConditionalVisualizationWrapper =
    InteractiveCanvasComponents?.CanvasVisualizationArea
      ? InteractiveCanvasComponents?.CanvasVisualizationArea
      : Box;

  return (
    <Box
      width="100%"
      height="100%"
      data-testid="presentation-container"
      alignItems="flex-end"
      flexDirection="row"
      justifyContent="center"
    >
      <ShowGroupDetailViewProvider>
        {visualizationState === 'view-grouped-results' ? (
          <GroupsWithVotesDataHandler />
        ) : visualizationState === 'view-grouped-responses' ? (
          <GroupsWithoutVotesDataHandler />
        ) : visualizationState === 'editor-empty' ? (
          <EditorEmptyState
            layout={layout}
            subType={subType}
            ConditionalVisualizationWrapper={ConditionalVisualizationWrapper}
          />
        ) : (
          <Box width="100%" height="100%">
            <TitleSectionDataHandler showTextSizeDropdown={true}>
              {shouldShowVoteCounterText && (
                <TitleVoteCounter
                  numberOfResponses={numberOfAnswers}
                  numberOfResponsesWithUpvotes={numberOfAnswersWithUpvotes}
                  shouldShowVotes={visualizationState === 'view-results'}
                />
              )}
            </TitleSectionDataHandler>
            <ConditionalVisualizationWrapper
              width="100%"
              flex="1 1 100%"
              p={1}
              extend={() => ({
                pointerEvents: 'auto',
              })}
            >
              <Visualization
                subType={subType}
                visualizationState={visualizationState}
                layout={layout}
                responses={responses}
              />
            </ConditionalVisualizationWrapper>
          </Box>
        )}
      </ShowGroupDetailViewProvider>
    </Box>
  );
};
